import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, KeyRound, RefreshCw } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/layout/PageHeader';
import { useCurrentUser } from '../auth';
import { ApiError } from '../../lib/api';
import type { ApiConnectionPlatform } from '../../types/apiConnections';
import type { DriftKind } from '../../lib/adAccountDrift';
import { getConnectionHealthReport, runConnectionHealthCheck } from './api';
import { formatStaleFor } from '../../lib/tokenHealth';
import {
  DRIFT_KIND_LABEL,
  DRIFT_KIND_VARIANT,
  driftReconnectHref,
  EMPTY_DRIFT_FILTER,
  EMPTY_HEALTH_FILTER,
  EMPTY_TOKEN_FILTER,
  filterDriftRows,
  filterHealthRows,
  filterTokenStaleRows,
  formatAdAccountList,
  formatRelative,
  HEALTH_PLATFORM_OPTIONS,
  healthReconnectHref,
  PLATFORM_LABEL,
  TOKEN_PLATFORM_OPTIONS,
  tokenReconnectHref,
} from './connectionHealthView';

// Connection Health & Drift (/connection-health, admin-only). ONE place to see
// which accounts need attention: live token FAILURES (from the community jobs'
// captured meta.failedAccounts) and ad-account config DRIFT (kyck fbAdAccountId
// ≠ the profile's scope), labelled distinctly. Polls every 60s.

const REPORT_KEY = ['connection-health', 'report'] as const;

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

function FacetSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-sm text-slate-600">
      <span>{label}</span>
      <select
        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ConnectionHealthPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const [healthFilter, setHealthFilter] = useState(EMPTY_HEALTH_FILTER);
  const [driftFilter, setDriftFilter] = useState(EMPTY_DRIFT_FILTER);
  const [tokenFilter, setTokenFilter] = useState(EMPTY_TOKEN_FILTER);

  const report = useQuery({
    queryKey: REPORT_KEY,
    queryFn: getConnectionHealthReport,
    enabled: me.role === 'admin',
    refetchInterval: 60_000,
  });

  const check = useMutation({
    mutationFn: runConnectionHealthCheck,
    onSuccess: (r) => {
      const ignored = r.transientIgnored > 0 ? `, ${r.transientIgnored} transient ignored` : '';
      toast.success(
        r.failing > 0 || r.recovered > 0
          ? `Checked — ${r.failing} failing, ${r.recovered} recovered${ignored}`
          : `Checked — no changes${ignored}`,
      );
      qc.invalidateQueries({ queryKey: REPORT_KEY });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't run the check."),
  });

  const health = useMemo(
    () => filterHealthRows(report.data?.health ?? [], healthFilter),
    [report.data, healthFilter],
  );
  const drift = useMemo(
    () => filterDriftRows(report.data?.drift ?? [], driftFilter),
    [report.data, driftFilter],
  );
  const tokenStale = useMemo(
    () => filterTokenStaleRows(report.data?.tokenStale ?? [], tokenFilter),
    [report.data, tokenFilter],
  );

  if (me.role !== 'admin') {
    return <p className="text-sm text-slate-600">Only admins can view connection health.</p>;
  }

  const totalHealth = report.data?.health.length ?? 0;
  const totalDrift = report.data?.drift.length ?? 0;
  const totalTokens = report.data?.tokenStale.length ?? 0;
  const tokenCheckAvailable = report.data?.tokenCheckAvailable ?? false;
  const tokenThresholdHours = report.data?.tokenStaleThresholdHours ?? 24;

  return (
    <div>
      <PageHeader
        title="Connection health & drift"
        description="Accounts (in Community Management) whose inbox feed is failing to load, plus ad accounts that have drifted from their Zernio profile."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => check.mutate()}
            disabled={check.isPending}
          >
            <RefreshCw className={`h-4 w-4 ${check.isPending ? 'animate-spin' : ''}`} />
            Check now
          </Button>
        }
      />

      {report.isLoading ? (
        <CenteredSpinner />
      ) : report.error ? (
        <p className="text-sm text-red-600">Couldn't load connection health.</p>
      ) : (
        <div className="space-y-10">
          {/* ---- Inbox feed failing (community-inbox READ failure) ---- */}
          <section>
            <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                <AlertTriangle className="h-4 w-4 text-rose-500" />
                Inbox feed failing
                <span className="text-sm font-normal text-slate-500">
                  ({health.length === totalHealth ? totalHealth : `${health.length} of ${totalHealth}`})
                </span>
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="search"
                  placeholder="Filter by name / brand / AM…"
                  className="w-56 rounded-md border border-slate-300 px-2 py-1 text-sm"
                  value={healthFilter.search}
                  onChange={(e) => setHealthFilter((f) => ({ ...f, search: e.target.value }))}
                />
                <FacetSelect<ApiConnectionPlatform | 'all'>
                  label="Platform"
                  value={healthFilter.platform}
                  onChange={(platform) => setHealthFilter((f) => ({ ...f, platform }))}
                  options={[
                    { value: 'all', label: 'All' },
                    ...HEALTH_PLATFORM_OPTIONS.map((p) => ({ value: p, label: PLATFORM_LABEL[p] })),
                  ]}
                />
              </div>
            </div>

            <p className="mb-3 max-w-3xl text-xs text-slate-500">
              The community inbox isn't pulling new activity for these accounts. Scoped to accounts in
              Community Management only. A failing feed does <span className="font-medium">not</span>{' '}
              always mean a dead connection — posting and ads can still work (this only measures the
              inbox read). A genuinely expired token would also fail the feed, so Reconnect is still
              the fix when the connection really is dead.
            </p>

            {totalHealth === 0 ? (
              <p className="text-sm text-slate-600">
                No accounts with a failing inbox feed. This is captured from the community poll / sweep
                / reconcile jobs (Community Management accounts only) — the next run refreshes it.
              </p>
            ) : health.length === 0 ? (
              <p className="text-sm text-slate-600">No failing inbox feeds match the filter.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Account</th>
                      <th className="px-4 py-2 font-medium">Brand / group</th>
                      <th className="px-4 py-2 font-medium">Account manager</th>
                      <th className="px-4 py-2 font-medium">Failing</th>
                      <th className="px-4 py-2 font-medium">Last failure</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {health.map((row) => (
                      <tr key={row.accountId} className="hover:bg-slate-50 align-top">
                        <td className="px-4 py-2">
                          <Link
                            to={`/accounts/${row.accountId}`}
                            className="font-medium text-slate-900 hover:underline"
                          >
                            {row.accountName}
                          </Link>
                          <div className="text-xs text-slate-500">{row.accountType}</div>
                        </td>
                        <td className="px-4 py-2 text-slate-600">
                          {row.brandName ?? '—'}
                          {row.groupName && <span className="text-slate-400"> · {row.groupName}</span>}
                        </td>
                        <td className="px-4 py-2 text-slate-600">{row.accountManagerName ?? '—'}</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-col gap-1">
                            {row.failing.map((f) => (
                              <div key={f.platform} className="flex items-center gap-2">
                                <Badge variant="danger">{PLATFORM_LABEL[f.platform]}</Badge>
                                <span
                                  className="text-xs text-slate-500"
                                  title={`${f.lastError ?? 'error'}${f.lastErrorCode ? ` (code ${f.lastErrorCode})` : ''}`}
                                >
                                  {f.lastError ?? 'error'}
                                  {f.failureCount > 1 && (
                                    <span className="text-slate-400"> ·×{f.failureCount}</span>
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td
                          className="px-4 py-2 text-slate-600"
                          title={fmtDateTime(row.mostRecentFailureAt)}
                        >
                          {formatRelative(row.mostRecentFailureAt)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button asChild size="sm" variant="outline" className="h-7">
                            <Link to={healthReconnectHref(row)}>Reconnect</Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ---- Tokens needing reconnection (stale OAuth expiry) ---- */}
          <section>
            <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                <KeyRound className="h-4 w-4 text-amber-500" />
                Tokens needing reconnection
                <span className="text-sm font-normal text-slate-500">
                  (
                  {!tokenCheckAvailable
                    ? 'unavailable'
                    : tokenStale.length === totalTokens
                      ? totalTokens
                      : `${tokenStale.length} of ${totalTokens}`}
                  )
                </span>
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="search"
                  placeholder="Filter by name / brand / AM…"
                  className="w-56 rounded-md border border-slate-300 px-2 py-1 text-sm"
                  value={tokenFilter.search}
                  onChange={(e) => setTokenFilter((f) => ({ ...f, search: e.target.value }))}
                />
                <FacetSelect<ApiConnectionPlatform | 'all'>
                  label="Platform"
                  value={tokenFilter.platform}
                  onChange={(platform) => setTokenFilter((f) => ({ ...f, platform }))}
                  options={[
                    { value: 'all', label: 'All' },
                    ...TOKEN_PLATFORM_OPTIONS.map((p) => ({ value: p, label: PLATFORM_LABEL[p] })),
                  ]}
                />
              </div>
            </div>

            <p className="mb-3 max-w-3xl text-xs text-slate-500">
              These connections' stored OAuth tokens expired more than{' '}
              {tokenThresholdHours}h ago and haven't refreshed — usually a revoked grant, which
              silently stops the data sync (reporting, ads, publishing) while the platform still
              reports itself <span className="font-medium">connected</span>. It's a{' '}
              <span className="font-medium">heuristic</span>, not a verdict: treat it as "may need
              reconnecting" and confirm before telling a client anything. A lapse of a few hours is
              normal and never listed here.
            </p>

            {!tokenCheckAvailable ? (
              <p className="text-sm text-slate-600">
                Token check unavailable — the connection read failed, so we can't tell whether any
                token is stale. This is <span className="font-medium">not</span> a clean bill of
                health; retry with "Check now" or reload shortly.
              </p>
            ) : totalTokens === 0 ? (
              <p className="text-sm text-slate-600">
                No stale tokens. Every active account's connections refreshed within the last{' '}
                {tokenThresholdHours}h.
              </p>
            ) : tokenStale.length === 0 ? (
              <p className="text-sm text-slate-600">No stale tokens match the filter.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Account</th>
                      <th className="px-4 py-2 font-medium">Brand / group</th>
                      <th className="px-4 py-2 font-medium">Account manager</th>
                      <th className="px-4 py-2 font-medium">Platform</th>
                      <th className="px-4 py-2 font-medium">Expired</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tokenStale.map((row) => (
                      <tr
                        key={`${row.accountId}:${row.platform}`}
                        className="hover:bg-slate-50 align-top"
                      >
                        <td className="px-4 py-2">
                          <Link
                            to={`/accounts/${row.accountId}?tab=connections`}
                            className="font-medium text-slate-900 hover:underline"
                          >
                            {row.accountName}
                          </Link>
                          <div className="text-xs text-slate-500">{row.accountType}</div>
                        </td>
                        <td className="px-4 py-2 text-slate-600">
                          {row.brandName ?? '—'}
                          {row.groupName && <span className="text-slate-400"> · {row.groupName}</span>}
                        </td>
                        <td className="px-4 py-2 text-slate-600">{row.accountManagerName ?? '—'}</td>
                        <td className="px-4 py-2">
                          <Badge variant="warning">{PLATFORM_LABEL[row.platform]}</Badge>
                        </td>
                        <td
                          className="px-4 py-2 text-slate-600"
                          title={`Token expired at ${row.tokenExpiresAt}`}
                        >
                          {formatStaleFor(row.staleHours)} ago
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button asChild size="sm" variant="outline" className="h-7">
                            <Link to={tokenReconnectHref(row)}>Reconnect</Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ---- Ad-account drift (config mismatch) ---- */}
          <section>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Ad-account drift
                <span className="text-sm font-normal text-slate-500">
                  ({drift.length === totalDrift ? totalDrift : `${drift.length} of ${totalDrift}`})
                </span>
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="search"
                  placeholder="Filter by name / ad account…"
                  className="w-56 rounded-md border border-slate-300 px-2 py-1 text-sm"
                  value={driftFilter.search}
                  onChange={(e) => setDriftFilter((f) => ({ ...f, search: e.target.value }))}
                />
                <FacetSelect<DriftKind | 'all'>
                  label="Kind"
                  value={driftFilter.kind}
                  onChange={(kind) => setDriftFilter((f) => ({ ...f, kind }))}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'drifted', label: DRIFT_KIND_LABEL.drifted },
                    { value: 'unset', label: DRIFT_KIND_LABEL.unset },
                    { value: 'profile-disconnected', label: DRIFT_KIND_LABEL['profile-disconnected'] },
                  ]}
                />
              </div>
            </div>

            {totalDrift === 0 ? (
              <p className="text-sm text-slate-600">
                No ad-account drift. Every live Meta account's kyck ad account matches its Zernio
                profile.
              </p>
            ) : drift.length === 0 ? (
              <p className="text-sm text-slate-600">No drift matches the filter.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Account</th>
                      <th className="px-4 py-2 font-medium">Brand / group</th>
                      <th className="px-4 py-2 font-medium">Drift</th>
                      <th className="px-4 py-2 font-medium">kyck ad account</th>
                      <th className="px-4 py-2 font-medium">Profile ad account(s)</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {drift.map((row) => (
                      <tr key={row.accountId} className="hover:bg-slate-50 align-top">
                        <td className="px-4 py-2">
                          <Link
                            to={`/accounts/${row.accountId}`}
                            className="font-medium text-slate-900 hover:underline"
                          >
                            {row.accountName}
                          </Link>
                          <div className="text-xs text-slate-500">{row.accountType}</div>
                        </td>
                        <td className="px-4 py-2 text-slate-600">
                          {row.brandName ?? '—'}
                          {row.groupName && <span className="text-slate-400"> · {row.groupName}</span>}
                        </td>
                        <td className="px-4 py-2">
                          <Badge variant={DRIFT_KIND_VARIANT[row.kind]}>
                            {DRIFT_KIND_LABEL[row.kind]}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-600">
                          {row.kyckAdAccountId ?? '—'}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-600">
                          {formatAdAccountList(row.profileAdAccountIds)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button asChild size="sm" variant="outline" className="h-7">
                            <Link to={driftReconnectHref(row)}>
                              {row.kind === 'unset' ? 'Set ad account' : 'Reconnect'}
                            </Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {report.data && (
            <p className="text-xs text-slate-400">
              Updated {fmtDateTime(report.data.generatedAt)}. Health is captured passively from the
              community jobs' fetch failures — no per-account probing.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
